import * as THREE from "three";
import type { AttackRejectReason, TargetId } from "@ict/shared";
import { EVENTS, type AppSocket } from "@/network/SocketClient";
import type { PlayerController } from "@/player/PlayerController";
import type { TargetsController } from "@/targets/TargetsController";

const NEAREST_FALLBACK_RANGE = 4.2;

export type RejectHandler = (reason: AttackRejectReason) => void;

/**
 * Client-side attack input. Combat is intentionally simple (per spec): a
 * single left-click tries to hit whatever target is aimed at (raycast) or,
 * failing that, whichever target is nearest within a generous range. The
 * server is the sole authority on whether the hit actually counts.
 */
export class CombatController {
  private raycaster = new THREE.Raycaster();
  private enabled = true;

  constructor(
    private readonly socket: AppSocket,
    private readonly camera: THREE.PerspectiveCamera,
    private readonly domElement: HTMLElement,
    private readonly player: PlayerController,
    private readonly targets: TargetsController,
    private readonly onReject: RejectHandler
  ) {
    this.domElement.addEventListener("click", this.onClick);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  private onClick = (): void => {
    if (!this.enabled) return;
    if (document.pointerLockElement !== this.domElement) return; // this click only requested pointer lock
    this.attemptAttack();
  };

  /** Public entry point for non-mouse input sources (touch attack button). */
  manualAttack(): void {
    if (!this.enabled) return;
    this.attemptAttack();
  }

  private attemptAttack(): void {
    const targetId = this.pickTarget();
    this.player.triggerAttack();
    if (!targetId) return;
    this.socket.emit(EVENTS.MISSION_ATTACK, { targetId });
  }

  private pickTarget(): TargetId | null {
    this.raycaster.setFromCamera(new THREE.Vector2(0, 0), this.camera);
    const candidates = this.targets.meshesForRaycast();
    const hits = this.raycaster.intersectObjects(
      candidates.map((c) => c.object),
      false
    );
    if (hits.length > 0) {
      const hitObject = hits[0].object;
      const found = candidates.find((c) => c.object === hitObject);
      if (found) return found.targetId;
    }

    const playerPos = this.player.getPosition();
    let nearestId: TargetId | null = null;
    let nearestDist = NEAREST_FALLBACK_RANGE;
    for (const c of candidates) {
      const d = this.targets.distanceFromPlayer(c.targetId, playerPos);
      if (d !== null && d < nearestDist) {
        nearestDist = d;
        nearestId = c.targetId;
      }
    }
    return nearestId;
  }

  attachServerListeners(): void {
    this.socket.on(EVENTS.TARGET_HIT, ({ targetId, hp, maxHp }) => {
      this.targets.applyHit(targetId, hp, maxHp);
    });
    this.socket.on(EVENTS.TARGET_DESTROYED, ({ targetId }) => {
      this.targets.applyDestroyed(targetId);
    });
    this.socket.on(EVENTS.ATTACK_REJECTED, ({ reason }) => {
      this.onReject(reason);
    });
  }

  dispose(): void {
    this.domElement.removeEventListener("click", this.onClick);
  }
}
